import { useState } from 'react'

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import {
  AutoStatsBase,
  AutoStatsDevice,
  AutoStatsGranularity,
  AutoStatsMode,
  AutoStatsRover,
  useAutoStatsBases,
  useAutoStatsDevices,
  useAutoStatsRovers,
} from 'api'
import { strings } from 'strings'

import { downloadCsv } from './csv'
import { MapListColumn, MapListView } from './MapListView'
import { useProvinces } from './provinces'

type ProvinceResolver = (lat: number, lng: number) => string | null

function provinceLabel(resolver: ProvinceResolver, ready: boolean, lat: number, lng: number): string {
  if (!ready) return '—'
  const p = resolver(lat, lng)
  return p ?? strings.unknownProvince
}

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
function toPersianDigits(n: number): string {
  return String(n).replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)])
}

export type BucketKind = 'bases' | 'rovers' | 'devices'

interface BucketDetailsDialogProps {
  open: boolean
  onClose: () => void
  title: string
  kind: BucketKind
  mode?: AutoStatsMode
  bucket?: string | null
  granularity?: AutoStatsGranularity
  from?: string
  to?: string
  csvFilename?: string
  raymand?: boolean
  baseIdAllowlist?: number[] | null
  deviceSerialAllowlist?: string[] | null
  roverUserIdAllowlist?: number[] | null
}

export function BucketDetailsDialog(props: BucketDetailsDialogProps) {
  const classes = useStyles()
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullScreen
      PaperProps={{ style: { direction: 'rtl' } }}
    >
      <DialogTitle className={classes.dialogTitle}>{props.title}</DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        {props.open && (
          <DialogBody
            kind={props.kind}
            mode={props.mode}
            bucket={props.bucket}
            granularity={props.granularity}
            from={props.from}
            to={props.to}
            csvFilename={props.csvFilename ?? props.kind}
            raymand={props.raymand}
            baseIdAllowlist={props.baseIdAllowlist}
            deviceSerialAllowlist={props.deviceSerialAllowlist}
            roverUserIdAllowlist={props.roverUserIdAllowlist}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} size="small" variant="outlined">
          {strings.close}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface BodyProps {
  kind: BucketKind
  mode?: AutoStatsMode
  bucket?: string | null
  granularity?: AutoStatsGranularity
  from?: string
  to?: string
  csvFilename: string
  raymand?: boolean
  baseIdAllowlist?: number[] | null
  deviceSerialAllowlist?: string[] | null
  roverUserIdAllowlist?: number[] | null
}

function DialogBody(props: BodyProps) {
  const classes = useStyles()
  const hint = counterHint(props.kind, props.mode)
  return (
    <div className={classes.bodyWrap}>
      <Typography className={classes.hint}>{hint}</Typography>
      <div className={classes.bodyContent}>
        {props.kind === 'bases' && <BasesTable {...props} />}
        {props.kind === 'rovers' && <RoversTable {...props} />}
        {props.kind === 'devices' && <DevicesTable {...props} />}
      </div>
    </div>
  )
}

function counterHint(kind: BucketKind, mode?: AutoStatsMode): string {
  if (kind === 'bases') {
    if (mode === 'activity') return strings.counterHintBasesActivity
    if (mode === 'growth') return strings.counterHintBasesGrowth
    return strings.counterHintBasesAll
  }
  if (kind === 'devices') {
    if (mode === 'activity') return strings.counterHintDevicesActivity
    if (mode === 'growth') return strings.counterHintDevicesGrowth
    return strings.counterHintDevicesAll
  }
  if (mode === 'activity') return strings.counterHintRoversActivity
  if (mode === 'growth') return strings.counterHintRoversGrowth
  return strings.counterHintRoversAll
}

interface TableProps {
  mode?: AutoStatsMode
  bucket?: string | null
  granularity?: AutoStatsGranularity
  from?: string
  to?: string
  csvFilename: string
  raymand?: boolean
  baseIdAllowlist?: number[] | null
  deviceSerialAllowlist?: string[] | null
  roverUserIdAllowlist?: number[] | null
}

function useQueryOpts(props: TableProps) {
  return {
    mode: props.mode,
    bucket: props.bucket ?? undefined,
    granularity: props.granularity,
    raymand: props.raymand,
  }
}

function rangeArgs(props: TableProps): [string | null, string | null] {
  return [props.from ?? null, props.to ?? null]
}

function BasesTable(props: TableProps) {
  const [from, to] = rangeArgs(props)
  const { bases: rawBases, loading, error } = useAutoStatsBases(from, to, useQueryOpts(props))
  const { ready: provincesReady, resolve } = useProvinces()

  const bases = props.baseIdAllowlist && rawBases
    ? (() => {
        const allow = new Set(props.baseIdAllowlist)
        return rawBases.filter((b) => allow.has(b.mountpointId))
      })()
    : rawBases

  const onExport = () =>
    exportBases(props.csvFilename, bases, (b) =>
      provinceLabel(resolve, provincesReady, b.latitude, b.longitude),
    )

  if (loading) return <StatusLoading />
  if (error) return <StatusError message={String(error.message ?? error)} />
  if (!bases || bases.length === 0) return <StatusEmpty />

  const columns: MapListColumn<AutoStatsBase>[] = [
    { label: strings.mountpoint, render: (b) => b.mountpoint },
    {
      label: strings.state,
      render: (b) => provinceLabel(resolve, provincesReady, b.latitude, b.longitude),
    },
    { label: strings.ownerDisplayName, render: (b) => b.ownerDisplayName },
    { label: strings.ownerPhoneNumber, ltr: true, render: (b) => b.ownerPhoneNumber },
    { label: strings.baseConnections, render: (b) => toPersianDigits(b.connectionCount) },
  ]

  return (
    <MapListView
      rows={bases}
      columns={columns}
      rowKey={(b) => b.mountpointId}
      getLocation={(b) => [b.latitude, b.longitude]}
      toolbar={<CsvToolbar onExport={onExport} />}
      markerColor="#1976d2"
    />
  )
}

function RoversTable(props: TableProps) {
  const classes = useStyles()
  const [from, to] = rangeArgs(props)
  const { rovers: rawRovers, loading, error } = useAutoStatsRovers(from, to, useQueryOpts(props))
  const { ready: provincesReady, resolve } = useProvinces()
  const [expandedAgents, setExpandedAgents] = useState<Set<number>>(new Set())

  const rovers = props.roverUserIdAllowlist && rawRovers
    ? (() => {
        const allow = new Set(props.roverUserIdAllowlist)
        return rawRovers.filter((r) => allow.has(r.userId))
      })()
    : rawRovers

  const roverProvince = (r: AutoStatsRover): string | null => {
    if (r.latitude === undefined || r.longitude === undefined) return null
    if (r.latitude === 0 && r.longitude === 0) return strings.unknownProvince
    return provinceLabel(resolve, provincesReady, r.latitude, r.longitude)
  }

  const hasLocation = !!rovers && rovers.some(
    (r) => r.latitude !== undefined && r.longitude !== undefined,
  )

  const onExport = () =>
    exportRovers(props.csvFilename, rovers, hasLocation ? roverProvince : undefined)

  if (loading) return <StatusLoading />
  if (error) return <StatusError message={String(error.message ?? error)} />
  if (!rovers || rovers.length === 0) return <StatusEmpty />

  const toggleAgents = (userId: number) => {
    setExpandedAgents((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  const columns: MapListColumn<AutoStatsRover>[] = [
    { label: strings.fullname, render: (r) => r.displayName },
    { label: strings.username, render: (r) => r.username },
    { label: strings.phoneNumber, ltr: true, render: (r) => r.phoneNumber },
    ...(hasLocation
      ? [
          {
            label: strings.state,
            render: (r: AutoStatsRover) => roverProvince(r) ?? '—',
          },
        ]
      : []),
    { label: strings.roverConnections, render: (r) => toPersianDigits(r.totalConnections) },
    {
      label: strings.lastAgent,
      ltr: true,
      render: (r) => (
        <span className={classes.agentTruncate} title={r.userAgent}>
          {r.userAgent}
        </span>
      ),
    },
    {
      label: strings.otherAgents,
      ltr: true,
      render: (r) => {
        const others = (r.userAgents ?? []).filter((a) => a && a !== r.userAgent)
        if (others.length === 0) return '-'
        const expanded = expandedAgents.has(r.userId)
        return (
          <div className={classes.otherAgents}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                toggleAgents(r.userId)
              }}
              className={classes.agentsToggle}
              aria-expanded={expanded}
            >
              <span>{toPersianDigits(others.length)}</span>
              <span className={classes.agentsToggleArrow}>
                {expanded ? '▲' : '▼'}
              </span>
            </button>
            {expanded && (
              <ul className={classes.agentList}>
                {others.map((a, i) => (
                  <li key={i} title={a}>{a}</li>
                ))}
              </ul>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <MapListView
      rows={rovers}
      columns={columns}
      rowKey={(r) => r.userId}
      getLocation={(r) => {
        if (r.latitude === undefined || r.longitude === undefined) return null
        if (r.latitude === 0 && r.longitude === 0) return null
        return [r.latitude, r.longitude]
      }}
      toolbar={<CsvToolbar onExport={onExport} />}
      markerColor="#7b1fa2"
    />
  )
}

function DevicesTable(props: TableProps) {
  const [from, to] = rangeArgs(props)
  const { devices: rawDevices, loading, error } = useAutoStatsDevices(from, to, useQueryOpts(props))
  const { ready: provincesReady, resolve } = useProvinces()

  const devices = props.deviceSerialAllowlist && rawDevices
    ? (() => {
        const allow = new Set(props.deviceSerialAllowlist)
        return rawDevices.filter((d) => allow.has(d.serial))
      })()
    : rawDevices

  const onExport = () =>
    exportDevices(props.csvFilename, devices, (d) =>
      provinceLabel(resolve, provincesReady, d.latitude, d.longitude),
    )

  if (loading) return <StatusLoading />
  if (error) return <StatusError message={String(error.message ?? error)} />
  if (!devices || devices.length === 0) return <StatusEmpty />

  const columns: MapListColumn<AutoStatsDevice>[] = [
    { label: strings.serial, ltr: true, render: (d) => d.serial },
    {
      label: strings.state,
      render: (d) => provinceLabel(resolve, provincesReady, d.latitude, d.longitude),
    },
    { label: strings.ownerDisplayName, render: (d) => d.ownerDisplayName },
    { label: strings.ownerPhoneNumber, ltr: true, render: (d) => d.ownerPhoneNumber },
    { label: strings.roverConnections, render: (d) => toPersianDigits(d.totalRoverConnections) },
  ]

  return (
    <MapListView
      rows={devices}
      columns={columns}
      rowKey={(d) => d.serial}
      getLocation={(d) => [d.latitude, d.longitude]}
      toolbar={<CsvToolbar onExport={onExport} />}
      markerColor="#198D10"
    />
  )
}

export function exportBases(
  filename: string,
  bases: AutoStatsBase[] | undefined,
  getProvince?: (b: AutoStatsBase) => string,
) {
  if (!bases || bases.length === 0) return
  downloadCsv(
    filename,
    [
      strings.mountpoint,
      strings.state,
      strings.ownerDisplayName,
      strings.username,
      strings.ownerPhoneNumber,
      strings.baseConnections,
    ],
    bases.map((b) => [
      b.mountpoint,
      getProvince ? getProvince(b) : '',
      b.ownerDisplayName,
      b.ownerUsername,
      b.ownerPhoneNumber,
      b.connectionCount,
    ]),
  )
}

export function exportRovers(
  filename: string,
  rovers: AutoStatsRover[] | undefined,
  getProvince?: (r: AutoStatsRover) => string | null,
) {
  if (!rovers || rovers.length === 0) return
  const withProvince = !!getProvince
  const headers = [
    strings.fullname,
    strings.username,
    strings.phoneNumber,
    ...(withProvince ? [strings.state] : []),
    strings.roverConnections,
    strings.lastAgent,
    strings.otherAgents,
  ]
  downloadCsv(
    filename,
    headers,
    rovers.map((r) => {
      const row: Array<string | number> = [
        r.displayName,
        r.username,
        r.phoneNumber,
      ]
      if (withProvince) row.push(getProvince!(r) ?? '')
      row.push(
        r.totalConnections,
        r.userAgent,
        (r.userAgents ?? [])
          .filter((a) => a && a !== r.userAgent)
          .join(' | '),
      )
      return row
    }),
  )
}

export function exportDevices(
  filename: string,
  devices: AutoStatsDevice[] | undefined,
  getProvince?: (d: AutoStatsDevice) => string,
) {
  if (!devices || devices.length === 0) return
  downloadCsv(
    filename,
    [
      strings.serial,
      strings.state,
      strings.ownerDisplayName,
      strings.username,
      strings.ownerPhoneNumber,
      strings.roverConnections,
    ],
    devices.map((d) => [
      d.serial,
      getProvince ? getProvince(d) : '',
      d.ownerDisplayName,
      d.ownerUsername,
      d.ownerPhoneNumber,
      d.totalRoverConnections,
    ]),
  )
}

function CsvToolbar({ onExport }: { onExport: () => void }) {
  const classes = useStyles()
  return (
    <div className={classes.toolbar}>
      <Button onClick={onExport} size="small" variant="outlined">
        {strings.exportCsv}
      </Button>
    </div>
  )
}

function StatusLoading() {
  const classes = useStyles()
  return (
    <div className={classes.status}>
      <CircularProgress size={20} />
      <Typography>{strings.loading}</Typography>
    </div>
  )
}

function StatusError({ message }: { message: string }) {
  const classes = useStyles()
  return (
    <Typography className={classes.status} color="error">
      {strings.error}: {message}
    </Typography>
  )
}

function StatusEmpty() {
  const classes = useStyles()
  return (
    <Typography className={classes.status}>{strings.dataNotFound}</Typography>
  )
}

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    padding: theme.spacing(1, 2),
    '& h2': {
      fontSize: '1rem',
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 1.5),
      '& h2': {
        fontSize: '0.95rem',
      },
    },
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    gap: theme.spacing(1),
    minHeight: 0,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0.75),
    },
  },
  bodyWrap: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    gap: theme.spacing(1),
  },
  bodyContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    justifyContent: 'center',
    width: '100%',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  hint: {
    fontSize: '0.8rem',
    color: '#555',
    background: '#f5f5f5',
    padding: theme.spacing(0.75, 1),
    borderRadius: 4,
    lineHeight: 1.6,
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.72rem',
      lineHeight: 1.5,
    },
  },
  agentTruncate: {
    display: 'inline-block',
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'bottom',
    [theme.breakpoints.down('xs')]: {
      maxWidth: 180,
    },
  },
  otherAgents: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
  agentsToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 4,
    padding: theme.spacing(0.25, 0.75),
    cursor: 'pointer',
    font: 'inherit',
    fontSize: '0.8rem',
    '&:hover': {
      background: '#f5f5f5',
    },
  },
  agentsToggleArrow: {
    fontSize: '0.65rem',
    color: '#888',
  },
  agentList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    maxWidth: 320,
    '& li': {
      padding: theme.spacing(0.25, 0),
      fontSize: '0.78rem',
      wordBreak: 'break-all',
      borderTop: '1px dashed #eee',
    },
    '& li:first-child': {
      borderTop: 'none',
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: 240,
    },
  },
}))
